import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfigureCustomArticleMode } from '@project/components/article/custom-article/configure/configure-custom-article-mode';
import { CustomArticleInfoVO, CustomArticleLibraryVO, CustomImageDataVO, SaveCustomArticleRequestVO } from '@project/components/article/model/vo/custom-article.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ValidatorFactory } from '@shared/validator.factory';
import { UserModel } from '@user/model/user.model';
import { NumberUtils } from '@shared/util/number-utils';
import { CustomArticleService } from '@article/service/custom-article.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { StringUtils } from '@shared/util/string-utils';
import { CustomArticleManager } from '@project/components/article/custom-article/custom-article.manager';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { CustomValidators } from '@shared/custom-validators';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { TextureAreaVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArrayUtils } from '@shared/util/array-utils';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { InsertArticleUtils } from '../../util/insert-article-utils';
import { InsertArticleService } from '../../service/insert-article.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../../service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Component to add/edit a custom article
 */
@Component({
  standalone: false,
  selector: 'app-configure-custom-article',
  templateUrl: './configure-custom-article.component.html',
})
export class ConfigureCustomArticleComponent implements OnInit {
  @Input() configureCustomArticleMode!: ConfigureCustomArticleMode;
  @Input() customArticleInfo?: CustomArticleInfoVO;
  private _customArticleUsage?: ArticleUsageVO | undefined;
  @Input() customArticleLibrary?: CustomArticleLibraryVO;
  @Input() articleAlreadyExists: boolean = false;
  @Output() public finish = new EventEmitter<CustomArticleInfoVO | undefined>();

  private readonly DEFAULT_ARTICLE_SIZE_VALUE: number = 100;
  private readonly DEFAULT_ARTICLE_OFFSET_VALUE: number = 0;
  private readonly MAX_ALLOWED_IMAGE_WIDTH: number = 2048;
  private readonly MAX_ALLOWED_IMAGE_HEIGHT: number = 2048;
  private readonly CUSTOM_ARTICLE_MIN_SIZE_VALUE: number = 0.01;
  private readonly CUSTOM_ARTICLE_MAX_SIZE_VALUE: number = 9999.99;
  private readonly CUSTOM_ARTICLE_NUMBER_MAX_LENGHT: number = 200;
  private readonly CUSTOM_ARTICLE_DESCRIPTION_MAX_LENGHT: number = 500;
  private readonly CUSTOM_ARTICLE_MAX_WIDTH_CONTAINER: number = 300;
  private readonly CUSTOM_ARTICLE_MAX_HEIGHT_CONTAINER: number = 200;

  private _imageFile: any;
  private _customImageData?: CustomImageDataVO;
  public imageUrl: any;
  public isProductionUser: boolean = false;
  public imageContainerWidth: number = 0;
  public imageContainerHeight: number = 0;
  public isError: boolean = false;

  editForm!: FormGroup;
  public title: string = "";

  public get customArticleUsage(): ArticleUsageVO | undefined {
    return this._customArticleUsage;
  }

  public set customArticleUsage(articleUsage: ArticleUsageVO | undefined) {
    this._customArticleUsage = articleUsage;
    if (articleUsage) {
      this.customArticleInfo = CustomArticleUtils.convertUsagePropertiesToCustomArticleInfo(articleUsage);
      this.getExistingImageFromCustomUsage(articleUsage);
    }
  }

  constructor(
    private formBuilder: FormBuilder,
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private customArticleService: CustomArticleService,
    private localeResourceManager: LocaleResourceManager,
    private containerService: ContainerService,
    private insertArticleUtils: InsertArticleUtils,
    private mountingRailHelper: MountingRailHelper,
    private projectArticleService: ProjectArticleService,
    private selectionModelService: SelectionModelService,
    private undoRedoHelper: UndoRedoHelper,
    private undoRedoUtils: UndoRedoUtils,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private _insertArticleService: InsertArticleService,
    private _applicationRefreshService: ApplicationRefreshService,
    private _detachArticleUsageService: DetachArticleUsageService,
    insertArticleService: InsertArticleService,
    private validatorFactory: ValidatorFactory,
  ) {
    this.customArticleService.saveCustomImage$.pipe(takeUntilDestroyed()).subscribe((imageHash:string) => this.onCustomImageSaved(imageHash));
    insertArticleService.articleInserted$.pipe(takeUntilDestroyed()).subscribe(() => this.close());
  }

  ngOnInit(): void {
    this.isProductionUser = this.userModel.user.isProduction();
    this.title = this.createCustomArticleTitleByMode();

    if (!this.customArticleInfo) {
      this.customArticleInfo = new CustomArticleInfoVO();
      this.customArticleInfo.userId = this.userModel.user.id;
      this.customArticleInfo.articleNumber = '';
      this.customArticleInfo.articleDescription = '';
      this.customArticleInfo.articleWidth = this.DEFAULT_ARTICLE_SIZE_VALUE;
      this.customArticleInfo.articleHeight = this.DEFAULT_ARTICLE_SIZE_VALUE;
      this.customArticleInfo.articleDepth = this.DEFAULT_ARTICLE_SIZE_VALUE;
      this.customArticleInfo.articleDepthOffset = this.DEFAULT_ARTICLE_OFFSET_VALUE;
    }

    const numberValidator = this.validatorFactory.number({
      min: this.CUSTOM_ARTICLE_MIN_SIZE_VALUE,
      max: this.CUSTOM_ARTICLE_MAX_SIZE_VALUE,
      precision: 2,
    });

    this.editForm = this.formBuilder.group({
      articleNumberInput: [
        this.customArticleInfo.articleNumber,
        [CustomValidators.trimRequired, CustomValidators.maxLength(this.CUSTOM_ARTICLE_NUMBER_MAX_LENGHT)],
      ],
      articleDescriptionInput: [this.customArticleInfo.articleDescription, CustomValidators.maxLength(this.CUSTOM_ARTICLE_DESCRIPTION_MAX_LENGHT)],
      widthInput: [this.formatDimensionValue(this.customArticleInfo.articleWidth), [numberValidator, CustomValidators.trimRequired]],
      heightInput: [this.formatDimensionValue(this.customArticleInfo.articleHeight), [numberValidator, CustomValidators.trimRequired]],
      depthInput: [this.formatDimensionValue(this.customArticleInfo.articleDepth), [numberValidator, CustomValidators.trimRequired]],
      depthOffsetInput: [
        this.formatDimensionValue(this.customArticleInfo.articleDepthOffset),
        [
          this.validatorFactory.number({
            min: -this.CUSTOM_ARTICLE_MAX_SIZE_VALUE,
            max: this.CUSTOM_ARTICLE_MAX_SIZE_VALUE,
          }),
          CustomValidators.trimRequired,
        ],
      ],
    });

    if (!StringUtils.isEmpty(this.customArticleInfo.imageHash)) {
      this.customArticleService
        .loadCustomArticleImage(this.customArticleInfo.userId, <string>this.customArticleInfo.imageHash)
        .subscribe((customImageData: CustomImageDataVO | undefined) => {

          if(!customImageData) {
            this.alertHelper.showError('imageLoader.error.title.image_not_loaded', 'imageLoader.error.message.image_invalid');
            return;
          }

          const img = new Image();
          img.onload = () => {
            if (img) {
              this.resizeImageContainer(img.width, img.height);
              this.imageUrl = img.src;
            }
          };
          img.src = `data:image/${customImageData.imageType};base64,${customImageData.imageData}`;
        });
    }
  }

  private parseDimensionValue(value: string): number {
    return UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(value));
  }

  protected async submit(): Promise<void> {
    if (!this.editForm.valid) {
      return;
    }
    this.updateCustomArticleConfiguration();
    if (!this.isError) {
      await this.createCustomArticleByMode();
    }
  }

  public close(): void {
    this.finish.emit(undefined);
  }

  public imageUpload(event: Event) {
    const reader: FileReader = new FileReader();
    // get the selected file from event
    reader.onloadend = (event) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > this.MAX_ALLOWED_IMAGE_WIDTH || img.height > this.MAX_ALLOWED_IMAGE_HEIGHT) {
          this.alertHelper.showError('imageLoader.error.title.image_too_big', 'imageLoader.error.message.image_too_big');
          return;
        } else {
          // Assign the result to variable for setting the src of image element
          this.imageUrl = reader.result;
          if (img) {
            this.resizeImageContainer(img.width, img.height);
          }
        }
      };
      img.src = reader.result as string;
    };
    const files = (<HTMLInputElement>event.target)?.files ?? []
    if (ArrayUtils.isNotEmpty(files) && /(png|jpg|jpeg)/.test(files[0].type)) {
      // only png, jpg, jpeg types are allowed
      this._imageFile = files[0];
      reader.readAsDataURL(this._imageFile);
    }
  }

  private resizeImageContainer(widthImage: number, heightImage: number): void {
    if (widthImage && heightImage) {
      const differenceWidth = widthImage / this.CUSTOM_ARTICLE_MAX_WIDTH_CONTAINER;
      const differenceHeight = heightImage / this.CUSTOM_ARTICLE_MAX_HEIGHT_CONTAINER;
      const maxDifference: number = Math.max(differenceWidth, differenceHeight);
      this.imageContainerWidth = Math.ceil(widthImage / maxDifference);
      this.imageContainerHeight = Math.ceil(heightImage / maxDifference);
    }
  }

  private async onCustomImageSaved(imageHash: string): Promise<void> {
    if (imageHash) {
      if (this.customArticleInfo) {
        this.customArticleInfo.imageHash = imageHash;
      }
      await this.proceedConnectCustomArticle();
    }
    this.close();
  }

  private async createCustomArticleByMode(): Promise<void> {
    switch (this.configureCustomArticleMode) {
      case ConfigureCustomArticleMode.CREATE_CUSTOM_ARTICLE:
        await this.saveCustomArticle();
        break;
      case ConfigureCustomArticleMode.EDIT_CUSTOM_ARTICLE:
      case ConfigureCustomArticleMode.VIEW_CUSTOM_ARTICLE:
        await this.connectCustomArticle();
        break;
    }
  }

  private createCustomArticleTitleByMode(): string {
    switch (this.configureCustomArticleMode) {
      case ConfigureCustomArticleMode.CREATE_CUSTOM_ARTICLE:
      case ConfigureCustomArticleMode.EDIT_CUSTOM_ARTICLE:
        return 'configureCustomArticlePopup.edit.title';
      case ConfigureCustomArticleMode.VIEW_CUSTOM_ARTICLE:
        return 'configureCustomArticlePopup.view.title';
      default:
        return "";
    }
  }

  private async saveCustomArticle(): Promise<void> {
    const articleNumber: string = this.customArticleInfo?.articleNumber ?? "";
    const articleInfoId: number = this.customArticleInfo?.id ?? NaN;
    if (this.customArticleInfo && !this.isArticleNumberExists(articleNumber, articleInfoId)) {
      const customArticleRequestVO: SaveCustomArticleRequestVO = new SaveCustomArticleRequestVO();
      customArticleRequestVO.articleInfo = this.customArticleInfo;
      customArticleRequestVO.imageData = this._customImageData;
      this.customArticleService.saveCustomArticle(customArticleRequestVO).subscribe((customArticleInfoVO: CustomArticleInfoVO) => {
        this.customArticleInfo = customArticleInfoVO;
        this.finish.emit(this.customArticleInfo);
      });
    } else {
      await this.alertHelper.showError('configureCustomArticlePopup.error.title.save_custom_article', 'configureCustomArticlePopup.error.message.save_custom_article');
      this.finish.emit();
    }
  }

  private isArticleNumberExists(articleNumber: string, articleInfoId: number): boolean {
    if (!this.customArticleLibrary || !this.customArticleLibrary.customArticleInfos) {
      return false;
    }
    for (const articleInfo of this.customArticleLibrary.customArticleInfos) {
      if (NumberUtils.isNumber(articleInfoId) && articleInfo.id === articleInfoId) {
        continue;
      }
      if (articleInfo.articleNumber === articleNumber) {
        return true;
      }
    }
    return false;
  }

  private updateCustomArticleConfiguration(): void {
    const newArticleNumber = this.editForm.value.articleNumberInput.trim();
    if (this.isArticleNumberExists(newArticleNumber, this.customArticleInfo?.id ?? NaN)) {
      this.isError = true;
      this.alertHelper.showError(
        'configureCustomArticlePopup.error.title.article_number_already_exists',
        'configureCustomArticlePopup.error.message.article_number_already_exists'
      );
      return;
    }
    if (!this.customArticleInfo) {
      this.alertHelper.showError('configureCustomArticlePopup.error.title.save_custom_article', 'configureCustomArticlePopup.error.message.save_custom_article');
      return;
    }
    this.isError = false;
    this.customArticleInfo.articleNumber = this.editForm.value.articleNumberInput.trim();
    this.customArticleInfo.articleDescription = this.editForm.value.articleDescriptionInput;
    this.customArticleInfo.articleWidth = this.parseDimensionValue(this.editForm.value.widthInput);
    this.customArticleInfo.articleHeight = this.parseDimensionValue(this.editForm.value.heightInput);
    this.customArticleInfo.articleDepth = this.parseDimensionValue(this.editForm.value.depthInput);
    this.customArticleInfo.articleDepthOffset = this.parseDimensionValue(this.editForm.value.depthOffsetInput);
    this.customArticleInfo.userId = this.userModel.user.id;

    if (this._imageFile) {
      const imageData = new CustomImageDataVO();
      imageData.userId = this.userModel.user.id;
      imageData.imageData = this.imageUrl ? this.imageUrl.substring(this.imageUrl.indexOf(',') + 1) : '';
      imageData.imageType = this._imageFile.type.replace('image/', '');

      if (imageData?.isValidImage()) {
        this._customImageData = imageData;
      } else {
        this.alertHelper.showError('imageLoader.error.title.image_not_loaded', 'imageLoader.error.message.image_invalid');
      }
    }
  }

  private saveCustomImage(): void {
    if(!this._customImageData || !this._customImageData.isValidImage()) {
      this.alertHelper.showError('imageLoader.error.title.image_not_loaded', 'imageLoader.error.message.image_invalid');
      return;
    }
    this.customArticleService.saveCustomArticleImage(this._customImageData);
  }

  private async connectCustomArticle(): Promise<void> {
    if (this._customImageData) {
      this.saveCustomImage();
    } else {
      await this.proceedConnectCustomArticle();
    }
  }

  private async proceedConnectCustomArticle(): Promise<void> {
    if (!this.customArticleUsage || !this.customArticleInfo) {
      return;
    }
    const customArticleManager: CustomArticleManager = new CustomArticleManager(
      this.insertArticleUtils,
      this.alertHelper,
      this.mountingRailHelper,
      this.projectArticleService,
      this.selectionModelService,
      this.undoRedoHelper,
      this.containerService,
      this.undoRedoUtils,
      this.insertArticleHelperFactory,
      this._insertArticleService,
      this._applicationRefreshService,
      this._detachArticleUsageService
    );
    await customArticleManager.recreateCustomArticle(this.customArticleInfo, this.customArticleUsage);
  }

  public canLoadImage(): boolean {
    return this.userModel.user && this.userModel.user.hasPermission("SAVE_CUSTOM_IMAGES");
  }

  private formatDimensionValue(value: number, roundValue: boolean = true): string {
    if (NumberUtils.isNotNumber(value)) {
      return "";
    }
    let valueInMillimeters: number = UnitConverter.fromTo('3d', 'mm', value);
    if (roundValue) {
      valueInMillimeters = this.roundNumberValue(valueInMillimeters);
    }
    return this.localeResourceManager.formatNumber(valueInMillimeters, false);
  }

  private roundNumberValue(value: number, precision: number = 2): number {
    return MathUtils.roundNumberValue(value, precision);
  }

  public getExistingImageFromCustomUsage(articleUsage: ArticleUsageVO) {
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
    const article: ArticleVO = container.article;
    const textureArea: TextureAreaVO = CustomArticleUtils.getCustomImageArea(article);
    if (!textureArea) {
      return null;
    }
    const textureConnection: Connection = container.findConnection(textureArea.name, '');
    this.imageUrl = textureConnection && textureConnection.imageBitmap ? textureConnection.imageBitmap.src : '';
    if (textureConnection.imageBitmap) {
      this.resizeImageContainer(textureConnection.imageBitmap.width, textureConnection.imageBitmap.height);
    }
  }

  public clearValidationAndClose(): void {
    if (!this.editForm.controls.articleNumberInput.touched) {
      this.editForm.controls.articleNumberInput.clearValidators();
      this.editForm.controls.articleNumberInput.updateValueAndValidity();
    }
    this.close();
  }
}
